import { FileText, ShieldCheck, FileSignature, Landmark } from 'lucide-react';
import SectionHead from './SectionHead';
import Reveal from './Reveal';
import { useLang } from '@/context/LanguageContext';

const ICONS = [FileText, ShieldCheck, FileSignature, Landmark];

const BAND_IMG =
  'https://images.unsplash.com/photo-1633351576904-d2d42ee54031?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMGluZGlhbiUyMHByb2Zlc3Npb25hbCUyMHNtYXJ0cGhvbmUlMjBkaWdpdGFsJTIwYmFua2luZ3xlbnwwfHx8fDE3OTA4NjQ0MzV8MA&ixlib=rb-4.1.0&q=85';

export default function HowItWorks() {
  const { t } = useLang();

  return (
    <section id="how" className="relative bg-abyss/40 py-24 lg:py-32" data-testid="how-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.how.eyebrow} title={t.how.title} />

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="how-steps">
          {t.how.steps.map((step, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.1}>
                <div
                  data-testid={`how-step-${i + 1}`}
                  className="group relative h-full rounded-2xl border border-white/10 bg-panel/60 p-7 transition-colors hover:border-pulse/40"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-4xl font-bold text-outline">0{i + 1}</span>
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-pulse/10 text-pulse transition-colors group-hover:bg-pulse group-hover:text-ink">
                      <Icon size={20} />
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-lg font-bold text-white">{step.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{step.d}</p>
                  <span className="absolute left-0 top-0 h-full w-0.5 origin-top scale-y-0 bg-pulse transition-transform duration-300 group-hover:scale-y-100" />
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-16">
          <div className="relative overflow-hidden rounded-3xl border border-white/10" data-testid="trust-band">
            <img
              src={BAND_IMG}
              alt="Instant UPI disbursal notification on a smartphone"
              className="h-72 w-full object-cover saturate-[0.7] lg:h-80"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/20" />
            <div className="absolute inset-0 flex flex-col justify-center px-7 sm:px-12">
              <h3 className="max-w-sm font-display text-2xl font-extrabold leading-tight text-white sm:text-3xl lg:text-4xl">
                {t.how.bandTitle}
              </h3>
              <div className="mt-6 flex flex-wrap gap-3">
                {t.how.bandStats.map((s, i) => (
                  <span
                    key={i}
                    data-testid={`trust-band-stat-${i}`}
                    className="rounded-full border border-pulse/30 bg-ink/70 px-4 py-1.5 font-mono text-xs font-semibold text-pulse backdrop-blur-md"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
